import { forgetUser, getAccessToken, login } from '@/auth/keycloak'
import { apiBaseUrl } from '@/config'
import { i18n } from '@/i18n'
import { track } from './activity'
import { selectedOrganization } from './org'
import type { ApiErrorBody } from './types'

/**
 * Thin fetch wrapper for mdm-service's admin API.
 *
 * Every request carries the Keycloak bearer token, the interface language (the
 * server translates its error messages with it) and the selected organization. A
 * 401 means the token expired or was revoked, and is surfaced as a typed error so
 * the router can send the user back through the login flow instead of showing an
 * empty table.
 *
 * The Apple device-protocol endpoints of the same service are not for browsers and
 * are never called from here.
 */

const BASE_URL = apiBaseUrl

/**
 * `VITE_MOCK=true npm run dev` answers every call from `src/mock` instead of the
 * API, and skips Keycloak (see the auth store). `import.meta.env.DEV` is false in
 * a production build, so the mock and its data are dropped from the bundle.
 */
const MOCK = import.meta.env.DEV && import.meta.env.VITE_MOCK === 'true'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** The session is gone; the caller should re-authenticate rather than retry. */
  get unauthenticated(): boolean {
    return this.status === 401
  }

  /** Authenticated but the role does not permit this action. */
  get forbidden(): boolean {
    return this.status === 403
  }

  /** Someone else changed the record first, or it clashes with an existing one. */
  get conflict(): boolean {
    return this.status === 409
  }
}

type Query = Record<string, string | number | boolean | undefined | null>

/**
 * The absolute URL of one API call.
 *
 * <p>`BASE_URL` may be a path (same origin, typically behind a reverse proxy) or a
 * full URL naming another host or port. Both have to survive: returning only
 * `pathname + search` would silently rewrite a configured
 * `http://host:8090/api/v1/...` into a same-origin `/api/v1/...`, and the requests
 * would go to whatever serves the console's static files.
 */
function buildUrl(path: string, query?: Query): string {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value))
      }
    }
  }
  return url.toString()
}

interface RequestOptions {
  query?: Query
  body?: unknown
  /** A multipart form (file uploads); sent as is, the browser sets the boundary. */
  form?: FormData
}

function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  return track(send<T>(method, path, options))
}

async function send<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  if (MOCK) {
    return (await import('@/mock')).mockRequest<T>(method, path, options.query ?? {}, options.form ?? options.body)
  }
  const response = await fetch(buildUrl(path, options.query), {
    method,
    headers: {
      Accept: 'application/json',
      ...(options.body && !options.form ? { 'Content-Type': 'application/json' } : {}),
      ...(await commonHeaders()),
    },
    body: options.form ?? (options.body ? JSON.stringify(options.body) : undefined),
  })

  if (!response.ok) {
    const error = await toApiError(response)
    await handleUnauthenticated(error)
    throw error
  }

  // 204: a delete, or any other call with nothing to say.
  if (response.status === 204) {
    return undefined as T
  }
  return (await response.json()) as T
}

/** Bearer token, interface language and selected organization. */
async function commonHeaders(): Promise<Record<string, string>> {
  const token = await getAccessToken()
  const organization = selectedOrganization.value
  return {
    'Accept-Language': i18n.global.locale.value,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(organization ? { 'X-Org-Id': organization } : {}),
  }
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = String(response.status)
  let message = response.statusText

  // A failing gateway or proxy answers with HTML, not the API's error shape, so
  // parsing is best-effort and the status is what we can always rely on. The
  // message is already translated by the server (Accept-Language); validation
  // errors arrive as a list of "field: message" lines.
  try {
    const body = (await response.json()) as ApiErrorBody
    code = body.error ?? code
    if (Array.isArray(body.message)) {
      message = body.message.join('; ')
    } else if (body.message) {
      message = body.message
    }
  } catch {
    // keep the status text
  }
  return new ApiError(response.status, code, message)
}

/**
 * A 401 means the token is gone, not that the screen is empty.
 *
 * <p>The local session is dropped and the user is sent back through Keycloak,
 * returning to the page they were on. Guarded by a flag because a screen that
 * fires four parallel requests would otherwise start four redirects.
 */
let reauthenticating = false

async function handleUnauthenticated(error: ApiError): Promise<void> {
  if (!error.unauthenticated || reauthenticating) {
    return
  }
  reauthenticating = true
  try {
    await forgetUser()
    await login(window.location.pathname + window.location.search)
  } catch (e) {
    reauthenticating = false
    console.error('Re-authentication failed', e)
  }
}

/** Downloads a file the API streams, such as an exported report or a signed profile. */
export function download(path: string, query?: Query): Promise<Blob> {
  return track(fetchBlob(path, query))
}

async function fetchBlob(path: string, query?: Query): Promise<Blob> {
  if (MOCK) {
    return new Blob([`mock download of ${path}`], { type: 'text/plain' })
  }
  const response = await fetch(buildUrl(path, query), {
    headers: await commonHeaders(),
  })
  if (!response.ok) {
    const error = await toApiError(response)
    await handleUnauthenticated(error)
    throw error
  }
  return response.blob()
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, { query }),
  post: <T>(path: string, body?: unknown, query?: Query) =>
    request<T>('POST', path, { body, query }),
  put: <T>(path: string, body?: unknown, query?: Query) =>
    request<T>('PUT', path, { body, query }),
  delete: <T>(path: string) => request<T>('DELETE', path),
  /** POSTs a multipart form, e.g. a certificate upload. */
  upload: <T>(path: string, form: FormData) => request<T>('POST', path, { form }),
}
